import { Form } from 'react-bootstrap';
import { MdCheckBox, MdChevronLeft, MdChevronRight, MdClose, MdDoNotDisturb } from 'react-icons/md';
import { CompanyMetadata, NetworkMetadata } from '@blisspointmedia/bpm-types/dist/LogsTracker';
import { Badge } from '@Components/Badge';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { Checkbox } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import { Offcanvas } from '@Components/Offcanvas';
import {
  LOG_TYPE_LABELS,
  LogType,
  LogTypeType,
  NetworkEdit,
  STATUS_BADGE_DESIGN,
  STATUS_LABELS
} from './logs-tracker-constants';
import {
  checkAll,
  clearAll,
  getNetworkStatus,
  getProgress,
  markRemainingDidNotRun
} from './logs-tracker-utils';

interface NetworkEditorProps {
  type: LogTypeType;
  week: string;
  network: NetworkMetadata | undefined;
  onEdit: (edit: NetworkEdit) => void;
  // Undefined when there is no previous/next network in the lane's current order.
  onPrevious?: () => void;
  onNext?: () => void;
  onClose: () => void;
  hasEdits: boolean;
  isSaving: boolean;
  onSave: () => void;
}

export const NetworkEditor = ({
  type,
  week,
  network,
  onEdit,
  onPrevious,
  onNext,
  onClose,
  hasEdits,
  isSaving,
  onSave
}: NetworkEditorProps) => (
  <Offcanvas
    show={Boolean(network)}
    onHide={onClose}
    width='640px'
    className='logs-network-editor'
    ariaLabel={network ? `Edit ${network.network}` : undefined}
    header={
      network && (
        <EditorHeader
          type={type}
          week={week}
          network={network}
          onPrevious={onPrevious}
          onNext={onNext}
          onClose={onClose}
        />
      )
    }
  >
    {network && (
      <div className='editor-body'>
        <Form.Control
          as='textarea'
          rows={3}
          placeholder='Network notes'
          value={network.networkNotes}
          onChange={e => onEdit({ networkNotes: e.target.value })}
        />
        <BulkActions type={type} network={network} onEdit={onEdit} />
        <CompanyTable type={type} network={network} onEdit={onEdit} />
        <div className='editor-footer'>
          <Button design={Design.NEUTRAL} appearance={Appearance.OUTLINED} onClick={onClose}>
            Done
          </Button>
          <Button design={Intent.SUCCESS} onClick={onSave} disabled={!hasEdits} loading={isSaving}>
            Save {LOG_TYPE_LABELS[type]}
          </Button>
        </div>
      </div>
    )}
  </Offcanvas>
);

interface EditorHeaderProps {
  type: LogTypeType;
  week: string;
  network: NetworkMetadata;
  onPrevious?: () => void;
  onNext?: () => void;
  onClose: () => void;
}

const EditorHeader = ({ type, week, network, onPrevious, onNext, onClose }: EditorHeaderProps) => {
  const status = getNetworkStatus(network);
  const { done, total } = getProgress(network);

  return (
    <div className='editor-header'>
      <div className='editor-title'>
        <span className='editor-network'>{network.network}</span>
        <span className='editor-subtitle'>
          {LOG_TYPE_LABELS[type]} · Week of {week}
        </span>
      </div>
      <Badge design={STATUS_BADGE_DESIGN[status]}>
        {STATUS_LABELS[status]} · {done}/{total}
      </Badge>
      <IconButton
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        aria-label='Previous network'
        disabled={!onPrevious}
        onClick={onPrevious}
      >
        <MdChevronLeft />
      </IconButton>
      <IconButton
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        aria-label='Next network'
        disabled={!onNext}
        onClick={onNext}
      >
        <MdChevronRight />
      </IconButton>
      <IconButton
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        aria-label='Close'
        onClick={onClose}
      >
        <MdClose />
      </IconButton>
    </div>
  );
};

interface BulkActionsProps {
  type: LogTypeType;
  network: NetworkMetadata;
  onEdit: (edit: NetworkEdit) => void;
}

const BulkActions = ({ type, network, onEdit }: BulkActionsProps) => (
  <div className='editor-bulk-actions'>
    <Button
      size={Size.SM}
      appearance={Appearance.OUTLINED}
      leadingElement={<MdCheckBox />}
      onClick={() => onEdit(checkAll(network))}
    >
      Check All
    </Button>
    {type === LogType.POST && (
      <Button
        size={Size.SM}
        appearance={Appearance.OUTLINED}
        leadingElement={<MdDoNotDisturb />}
        onClick={() => onEdit(markRemainingDidNotRun(network))}
      >
        Mark Remaining DNR
      </Button>
    )}
    <Button
      size={Size.SM}
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      onClick={() => onEdit(clearAll(network))}
    >
      Clear All
    </Button>
  </div>
);

interface CompanyTableProps {
  type: LogTypeType;
  network: NetworkMetadata;
  onEdit: (edit: NetworkEdit) => void;
}

const CompanyTable = ({ type, network, onEdit }: CompanyTableProps) => {
  const isPost = type === LogType.POST;

  return (
    <table className='editor-companies'>
      <thead>
        <tr>
          <th className='check-column'>Log In</th>
          {isPost && (
            <th className='check-column' title='Did Not Run'>
              DNR
            </th>
          )}
          <th>Company</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        {Object.values(network.companies).map(company => (
          <CompanyRow
            key={company.company}
            company={company}
            isPost={isPost}
            onEdit={patch => onEdit({ companies: { [company.company]: patch } })}
          />
        ))}
      </tbody>
    </table>
  );
};

interface CompanyRowProps {
  company: CompanyMetadata;
  isPost: boolean;
  onEdit: (patch: Partial<CompanyMetadata>) => void;
}

const CompanyRow = ({ company, isPost, onEdit }: CompanyRowProps) => (
  <tr>
    <td className='check-column'>
      <Checkbox
        aria-label={`${company.company} log in`}
        checked={company.checked}
        onChange={e => onEdit({ checked: e.target.checked })}
      />
    </td>
    {isPost && (
      <td className='check-column'>
        <Checkbox
          aria-label={`${company.company} did not run`}
          checked={company.didNotRun}
          onChange={e => onEdit({ didNotRun: e.target.checked })}
        />
      </td>
    )}
    <td className='company-name'>{company.company}</td>
    <td>
      <Form.Control
        as='textarea'
        rows={1}
        value={company.companyNotes}
        onChange={e => onEdit({ companyNotes: e.target.value })}
      />
    </td>
  </tr>
);
